import {
  Dialog,
  DialogContent,
  DialogHeader
} from "@gettactic/components/src/primitives/Dialog/Dialog";
import { useOneSchedule } from "@/lib/hooks/useOneSchedule";
import { cn } from "@gettactic/helpers/src/css/cn";
import { scheduleResourceStyling, primaryScheduleResource } from "@/lib/utils";
import { ISchedule } from "@gettactic/api";
import { useWindowSize } from "@gettactic/helpers/src/hooks/useWindowSize";
import Image from "next/image";
import { ReserveEdit } from "./ReserveEdit/ReserveEdit";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/lib/hooks/useCustomParkingVerbiage";
import { DelegatedPermissionRequest } from "./Reserve/DelegatedPermissionRequest";
import { useRoomReservationWeb, useAuthenticated } from "@gettactic/hooks";
import { MobileDrawer } from "@cmp-src/mobile/MobileDrawer/MobileDrawer";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";
import { ReserveFormProvider } from "./Reserve/ReserveFormProvider";

type Props = {
  editModal: boolean;
  toggleModal: () => void;
  schedule: ISchedule;
  canRespondCancel?: boolean;
  respondCancel?: () => void;
  shouldFetchSchedule?: boolean;
};

export function ReserveEditModal({
  editModal = false,
  toggleModal,
  schedule,
  canRespondCancel,
  respondCancel,
  shouldFetchSchedule = false
}: Props) {
  const browserWindowSize = useWindowSize();
  const browserHeight = browserWindowSize.height;

  const { data: updatedScheduleData, isLoading: scheduleLoading } =
    useOneSchedule({
      id: schedule.id,
      options: {
        enabled: !!schedule && shouldFetchSchedule,
        queryKey: []
      },
      defaultSchedule: schedule
    });

  const scheduleToUse = shouldFetchSchedule ? updatedScheduleData : schedule;

  const customParkingVerbiage = useCustomParkingVerbiage();
  const resourceType = schedule?.resource?.type ?? "desk";
  const styling = scheduleResourceStyling(
    resourceType,
    customParkingVerbiage !== null
  );

  // Get office from schedule for ReserveFormProvider
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const primaryResource = scheduleToUse?.slots
    ? primaryScheduleResource(scheduleToUse.slots)
    : scheduleToUse?.resource;
  const office = primaryResource?.office_id
    ? authenticatedUser?.offices?.byId?.[primaryResource.office_id]
    : undefined;

  const {
    requireDelegatedPermission,
    msAuthUrl,
    onRoomChange,
    onOrganizerChange,
    organizerRequiresDelegatedAccess,
    hasMSCalendar,
    hasGoogleCalendar
  } = useRoomReservationWeb({
    isOpen: editModal,
    isRoomReservation: resourceType === "meeting_room",
    defaultOrganizerId: scheduleToUse?.organizer_id
  });

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const contentHeader = (
    <div
      className={`${
        styling?.style
      } h-[80px] w-full overflow-hidden rounded-t-lg p-4`}
    >
      <div className="flex h-full items-center justify-between">
        <h4 className="text-2xl font-bold text-tertiary">Edit Reservation</h4>
        <div>
          <div className="flex h-full items-center justify-center">
            <Image
              src={styling?.image ?? ""}
              width={140}
              height={115}
              alt={styling?.alt ?? ""}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const content = (
    <>
      {scheduleToUse ? (
        <div
          style={{
            maxHeight: browserHeight ? `${browserHeight - 200}px` : "32rem"
          }}
          className={cn(
            "max-w-[32rem] px-4 ",
            browserHeight && browserHeight < 700 ? "overflow-y-auto" : ""
          )}
        >
          <ReserveFormProvider resourceType={resourceType} office={office}>
            <ReserveEdit
              schedule={scheduleToUse}
              onClose={toggleModal}
              canRespondCancel={canRespondCancel}
              respondCancel={respondCancel}
              isLoading={shouldFetchSchedule && scheduleLoading}
              onRoomChange={onRoomChange}
              onOrganizerChange={onOrganizerChange}
              organizerRequiresDelegatedAccess={
                organizerRequiresDelegatedAccess
              }
              hasMSCalendar={hasMSCalendar}
              hasGoogleCalendar={hasGoogleCalendar}
            />
          </ReserveFormProvider>
        </div>
      ) : null}
    </>
  );

  if (!isMd) {
    return (
      <MobileDrawer
        isOpen={editModal}
        size={"fit"}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            toggleModal?.();
          }
        }}
        contentUnderGrabber
        scrollableContent
        className="v2-reservation"
      >
        {contentHeader}
        <div className={"pt-6"} />
        {content}
        <div className={"pt-6"} />
      </MobileDrawer>
    );
  }

  return (
    <>
      <Dialog open={editModal} onOpenChange={toggleModal}>
        {requireDelegatedPermission ? (
          <DelegatedPermissionRequest
            msAuthUrl={msAuthUrl}
            onClose={toggleModal}
          />
        ) : (
          <DialogContent
            className="v2-reservation max-w-[32rem] px-0 pt-0"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <DialogHeader>{contentHeader}</DialogHeader>
            {content}
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
